import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Token ${token}` }),
  };
};

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function AddStaffDialog({ open, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "",
    entry_date: new Date().toISOString().split("T")[0],
    apartment: "",
    phone_number: "",
    address: "",
    emergency_contact: "",
    emergency_contact_phone: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
      const loadApartments = async () => {
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`, {
            headers: getAuthHeaders(),
          });

          const data = await response.json();
          if (response.ok) {
            setApartments(data);
          }
        } catch (e) {
          console.error("Failed to load apartments", e);
        }
      };

      loadApartments();
    }, []);

  const handleSave = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/staff/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok) {
        onSaved?.();
        onClose();
      } else {
        setError(data.detail || "Failed to create staff member");
      }
    } catch (err) {
      console.error(err);
      setError("Network error while saving staff");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Staff Member</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="First Name"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Apartment</InputLabel>
            <Select
              value={formData.apartment}
              onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
            >
              {apartments.map((apt) => (
                <MenuItem key={apt.id} value={apt.id}>
                  {apt.apartment_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Entry Date"
            type="date"
            value={formData.entry_date}
            onChange={(e) =>
              setFormData({ ...formData, entry_date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Phone Number"
            value={formData.phone_number}
            onChange={(e) =>
              setFormData({ ...formData, phone_number: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Address"
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Emergency Contact"
            value={formData.emergency_contact}
            onChange={(e) =>
              setFormData({ ...formData, emergency_contact: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Emergency Contact Phone"
            value={formData.emergency_contact_phone}
            onChange={(e) =>
              setFormData({ ...formData, emergency_contact_phone: e.target.value })
            }
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !formData.username || !formData.password}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
